import { type ReactNode } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';

import type { UserStandardTreeNode } from '../../utils/userStandardsTree';
import { CodeChip, StatusBadge } from '../atoms';

interface LibraryNodeRowProps {
  node: UserStandardTreeNode;
  depth: number;
  dragHandle: ReactNode;
  expanded: boolean;
  selected: boolean;
  onToggleExpand: (id: string) => void;
  onSelect: (node: UserStandardTreeNode) => void;
}

export function LibraryNodeRow({
  node,
  depth,
  dragHandle,
  expanded,
  selected,
  onToggleExpand,
  onSelect,
}: LibraryNodeRowProps) {
  const isFolder = node.node_type === 'folder';
  const isImported = Boolean(node.csp_standard_id);
  const hasChildren = node.children.length > 0;

  return (
    <Box
      onClick={() => onSelect(node)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        pl: `${depth * 22}px`,
        pr: 1,
        py: 0.5,
        minHeight: 36,
        borderRadius: 1,
        cursor: 'pointer',
        bgcolor: selected ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
      }}
    >
      {/* Fixed-width slot for the drag handle so its width never shifts alignment —
          indentation must depend on depth alone. */}
      <Box sx={{ width: 30, flex: 'none', display: 'flex', justifyContent: 'center' }}>
        {dragHandle}
      </Box>
      <IconButton
        size="small"
        sx={{ visibility: hasChildren ? 'visible' : 'hidden', p: 0.25, flex: 'none' }}
        onClick={(event) => {
          event.stopPropagation();
          onToggleExpand(node.id);
        }}
        aria-label={expanded ? 'Collapse' : 'Expand'}
      >
        <ChevronRightIcon
          fontSize="small"
          sx={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}
        />
      </IconButton>

      {isFolder ? (
        <FolderOutlinedIcon fontSize="small" color="action" sx={{ flex: 'none' }} />
      ) : (
        <DescriptionOutlinedIcon fontSize="small" color="action" sx={{ flex: 'none' }} />
      )}

      {!isFolder && node.code && <CodeChip code={node.code} sx={{ flex: 'none' }} />}
      <Typography
        variant="body2"
        noWrap
        sx={{ fontWeight: isFolder ? 600 : 400, flex: 1, minWidth: 0 }}
      >
        {node.title}
      </Typography>
      {!isFolder && <StatusBadge variant={isImported ? 'imported' : 'custom'} />}
    </Box>
  );
}

export default LibraryNodeRow;
